import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Eye, Calendar, Clock, MapPin, Loader2, Video, Phone } from "lucide-react";
import { slotService } from "../services";
import { InterviewerSlots, RemoteCallManagement } from "./Interviewer";
import VideoUpload from "../components/VideoUpload";
import VideoLibrary from "../components/VideoLibrary";
import useInterviewerDashboard from "../hooks/stores/useInterviewerDashboard";

export default function InterviewerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardStats, setDashboardStats] = useState({
    totalSlots: 0,
    upcomingSlots: 0,
    uniqueClients: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const { activeTab, setActiveTab } = useInterviewerDashboard();

  // Initialize activeTab from navigation state
  useEffect(() => {
    const initialTab = location.state?.activeTab || 'slots';
    setActiveTab(initialTab);
  }, [location.state?.activeTab, setActiveTab]);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setIsLoading(true);
        const response = await slotService.getAllSlotsForInterviewer();
        
        if (response && response.slots) {
          const slots = response.slots;
          const upcomingSlots = slots.filter(slot => new Date(slot.slot_date) >= new Date());
          const uniqueClients = new Set(slots.map(slot => slot.user_id)).size;
          
          setDashboardStats({
            totalSlots: response.total || slots.length,
            upcomingSlots: upcomingSlots.length,
            uniqueClients: uniqueClients
          });
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-[2rem] font-semibold text-foreground mb-2">
            Interviewer Dashboard
          </h1>
          <p className="text-sm sm:text-base font-normal text-muted-foreground">
            Manage your assigned sessions and client interactions
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          {/* Desktop Tab Navigation - Full Width */}
          <div className="hidden md:block">
          <div className="flex space-x-1 bg-secondary border border-secondary-border rounded-lg p-1">
            <button
              onClick={() => setActiveTab('slots')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'slots'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Sessions
            </button>
              <button
                onClick={() => setActiveTab('remote-calls')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'remote-calls'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <Phone className="h-4 w-4 mr-2" />
                Remote Calls
              </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'upload'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <Video className="h-4 w-4 mr-2" />
              Upload Videos
            </button>
            <button
              onClick={() => {
                setActiveTab('library');
              }}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'library'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <Eye className="h-4 w-4 mr-2" />
              Video Library
            </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'slots' && (
        <div className="bg-secondary border border-secondary-border rounded-lg sm:rounded-xl overflow-hidden">
          <InterviewerSlots />
        </div>
        )}

        {activeTab === 'remote-calls' && (
          <div className="bg-secondary border border-secondary-border rounded-lg sm:rounded-xl overflow-hidden">
                          <RemoteCallManagement />
        </div>
        )}

        {activeTab === 'upload' && (
          <div className="bg-secondary border border-secondary-border rounded-lg sm:rounded-xl p-4 sm:p-6">
            <VideoUpload />
          </div>
        )}

        {activeTab === 'library' && (
          <div className="bg-secondary border border-secondary-border rounded-lg sm:rounded-xl p-4 sm:p-6">
            <VideoLibrary />
          </div>
        )}
      </div>
    </div>
  );
} 